import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Form, Input, Select, SelectProps, Tag } from "antd";
import { generateErrorMessage } from "src/utils/error-message";
import FormLabel from "../FormLabel/FormLabel";
import { MapCompanyIdArea } from "src/entity/company/company-response.entity";
import { FormInstance, RuleObject } from "antd/es/form";
import PlaceHolderDashed from "../PlaceHolderDashed/PlaceHolderDashed";
import { componentPalette } from "src/theme/component-palette";
import { CloseOutlined } from "@ant-design/icons";
import VerticalCenter from "src/components/WrapCenter/VerticalCenter";
import { BsTrashFill } from "react-icons/bs";
import "./GroupCompanyArea.css";

type TagRender = SelectProps["tagRender"];

interface GrouCompanyAreaProps {
  index: number;
  item: MapCompanyIdArea;
  validateCompanyArea: (rule: RuleObject, val: any) => void;
  debounceHandleInput: any;
  onUpdateKeyVal: any;
  stateDataAreas: SelectProps["options"];
  manageMapCompanyArea: (key: string) => void;
  companyForm: FormInstance;
}

const GroupCompanyArea: React.FC<GrouCompanyAreaProps> = ({
  index,
  item,
  validateCompanyArea,
  debounceHandleInput,
  onUpdateKeyVal,
  stateDataAreas,
  manageMapCompanyArea,
  companyForm,
}) => {
  const selectRef: any = useRef(null);
  const [inputHeight, setInputHeight] = useState("auto");
  const [cnt, setCnt] = useState(0);

  // Function to update the height of the input based on the select's height
  const updateInputHeight = () => {
    if (selectRef.current) {
      setInputHeight(selectRef?.current?.nativeElement?.offsetHeight);
    }
  };
  useEffect(() => {
    // Update height initially
    updateInputHeight();
  }, [cnt, selectRef?.current?.nativeElement?.offsetHeight]);

  useEffect(() => {
    // Add event listener to update height whenever the window is resized
    window.addEventListener("resize", updateInputHeight);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", updateInputHeight);
    };
  }, []);

  const tagRender: TagRender = (props) => {
    const { label, closable, onClose } = props;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        color={componentPalette.redPeriodDate}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        closeIcon={<CloseOutlined style={{ color: componentPalette.black }} />}
        style={{ marginInlineEnd: 4, marginBottom: 1.5, marginTop: 1.5 }}
      >
        <span style={{ color: componentPalette.black }}>{label}</span>
      </Tag>
    );
  };

  const parseMixTypeArrayToStringArray = (arr: string[]) => {
    return arr.map((item) => {
      try {
        // Try to parse the item as JSON
        const parsed = JSON.parse(item);

        // If parsing is successful and the parsed object has an areaValue property, return it
        if (parsed && parsed.areaValue) {
          return parsed.areaValue;
        } else {
          return item;
        }
      } catch (e) {
        // If JSON.parse throws an error, it means the item is a plain string
        return item;
      }
    });
  };

  const getAreaValueFromString = (str: string) => {
    try {
      const result = JSON.parse(str);
      if (result && result.areaValue) {
        return result.areaValue;
      } else {
        return str;
      }
    } catch (error) {
      return str;
    }
  };

  // Output: ["TEST 00000000", "TEST 1111111111", "TEST 2222222", "TEST 3333333", "TEST 4444444"]

  const isDuplicateArea = (listAreas: string[]) => {
    if (!listAreas || !listAreas.length || listAreas.length < 2) return false;

    const lastValue = getAreaValueFromString(listAreas[listAreas.length - 1]);

    const processArray = listAreas.slice(0, listAreas.length - 1);
    const oldValueArray = parseMixTypeArrayToStringArray(processArray);
    // console.log("OldValueArray: ", oldValueArray);
    let isDuplicate = false;

    for (const item of oldValueArray) {
      if (item.toLowerCase().trim() === lastValue.toLowerCase().trim()) {
        isDuplicate = true;
        break;
      }
    }
    return isDuplicate;
  };
  //   console.log("Select Height , cnt : ", { inputHeight, cnt, item, selectHeight: selectRef?.current?.nativeElement?.offsetHeight || null });
  //   console.log("Form getFieldValues :: ", companyForm.getFieldsValue());
  return (
    <Col xs={24} md={24} key={"row-map-comp-area-" + index} className={index > 0 ? "-mt-8" : ""}>
      <Row>
        <Col span={12}>
          <Form.Item
            name={item.key + "-companyId"}
            label={<FormLabel label={index == 0 ? "Company ID" : ""} />}
            rules={[
              {
                required: true,
                message: generateErrorMessage("Company ID"),
              },
              {
                validator: (rule, val) => validateCompanyArea(rule, val),
              },
            ]}
          >
            <Input
              maxLength={50}
              onChange={(e) => debounceHandleInput(item.key, "companyId", e.target.value)}
              style={{ height: inputHeight, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Row className="w-full" id="area-selector">
            <Form.Item
              className="w-5/6"
              name={item.key + "-areaId"}
              label={<FormLabel label={index == 0 ? "Area" : ""} />}
              rules={[
                {
                  required: true,
                  message: generateErrorMessage("Area"),
                },
              ]}
            >
              <Select
                ref={selectRef}
                mode="tags"
                placeholder={<PlaceHolderDashed wording="Add Area" />}
                className="w-full"
                onChange={(e) => {
                  const isDuplicate = isDuplicateArea(e);
                  if (!isDuplicate) onUpdateKeyVal(item.key, "area", e);
                  else {
                    companyForm.setFieldValue(item.key + "-areaId", e.slice(0, e.length - 1));
                  }
                  setCnt(cnt + 1);
                }}
                tagRender={tagRender}
                options={stateDataAreas}
              />
            </Form.Item>

            <div className="w-1/6">
              <VerticalCenter styles={{ alignItems: "center" }}>
                <BsTrashFill
                  size={20}
                  color={"darkgrey"}
                  onClick={() => manageMapCompanyArea(item.key)}
                  style={{ cursor: "pointer", marginTop: 7.5 }}
                />
              </VerticalCenter>
            </div>
          </Row>
        </Col>
      </Row>
    </Col>
  );
};

export default GroupCompanyArea;
